import { Component, OnInit } from '@angular/core';
import { Activity } from '../../models/activity.model';
import { DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogManagerService } from '../../services/dialog-manager.service';
import { environment } from '../../../environments/environment';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ActivityTypeComponent } from '../activity-type/activity-type.component';
import { ActivityService } from 'src/app/services/activity.service';
import { ConfirmationService } from 'primeng/api';
import { AttachmentsComponent } from 'src/app/shared/attachments/attachments.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NotificationService } from 'src/app/services/notification.service';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';
import { FeedbackFormComponent } from '../menu/feedback/feedback-form/feedback-form.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-activity-item',
  standalone: true,
  imports: [
    DatePipe,
    DialogModule,
    EditorModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    ActivityTypeComponent,
    AttachmentsComponent,
    ConfirmDialogModule,
    SkeletonModule,
    FeedbackFormComponent,
  ],
  templateUrl: './activity-item.component.html',
  styleUrl: './activity-item.component.scss',
  providers: [ConfirmationService],
})
export class ActivityItemComponent implements OnInit {
  accessLevel = environment.accessLevel;
  activity: Activity | undefined = undefined;
  richTextControl = new FormControl('');
  remarks: string | undefined = '';
  editEnabled = false;
  loading = true;

  constructor(
    private dialogManager: DialogManagerService,
    private dialogConfig: DynamicDialogConfig,
    private activityService: ActivityService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.intializeActivityDetails();
  }

  intializeActivityDetails() {
    this.activityService
      .getActivityById(this.dialogConfig.data.activity)
      .subscribe({
        next: (activity) => {
          this.activity = activity;
          this.initializeRemarks();
          this.enableEdit();
          this.loading = false;
        },
        error: (error) => {
          this.notificationService.error(
            `Aktivität konnte nicht geladen werden.`,
            error,
          );
          this.loading = false;
          this.closeDialog({ success: false });
        },
      });
  }

  enableEdit(): void {
    const type = this.activity?.activityType.typeName;
    const editables = [
      'Email',
      'AusgehendeEmail',
      'EingehendeEmail',
      'Beratungsgespräch',
      'Abwesenheit',
    ];

    if (editables.includes(type)) {
      this.editEnabled = true;
    }
  }

  // Bearbeiten ist projektweit erst ab accessLevel > 1 freigegeben, weil es
  // laut Alt-Kommentar fehlerbehaftet ist. Abwesenheiten sind davon bewusst
  // ausgenommen, damit Krankmeldungen auch in Produktion korrigiert werden können.
  get canEditActivity(): boolean {
    if (!this.editEnabled) {
      return false;
    }

    if (this.activity?.activityType.typeName === 'Abwesenheit') {
      return true;
    }

    return this.accessLevel > 1;
  }

  initializeRemarks(): void {
    if (this.activity?.activityType.typeName == 'Notiz') {
      this.remarks = this.activity.note!.remarks;
    } else if (this.activity?.activityType.typeName == 'Kontakt') {
      this.remarks = this.activity.contact!.remarks;
    } else if (this.activity?.activityType.typeName == 'Beratungsgespräch') {
      this.remarks = this.activity?.consultation!.remarks;
    } else if (this.activity?.voucher?.freitext) {
      this.remarks = this.activity?.voucher?.freitext;
    } else if (
      this.activity?.activityType.typeName === 'Email' ||
      this.activity?.activityType.typeName === 'EingehendeEmail' ||
      this.activity?.activityType.typeName === 'AusgehendeEmail'
    ) {
      this.remarks = this.activity.email!.emailText;
    } else {
      this.remarks = '';
    }

    setTimeout(() => {
      if (this.remarks) {
        this.richTextControl.setValue(this.remarks);
      } else {
        this.richTextControl.setValue('');
      }
    }, 0);
  }

  confirmDelete(event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      message: `Möchten Sie diese Aktivität wirklich löschen?`,
      accept: () => {
        this.deleteActivity();
      },
    });
  }

  deleteActivity(): void {
    if (!this.activity) {
      return;
    }

    const activityId = this.activity.activityId;
    this.activityService.deleteActivity(activityId).subscribe({
      next: (response) => {
        this.notificationService.success(
          `Aktivität erfolgreich gelöscht`,
          response,
        );
        this.activityService.removeActivityFromCache(activityId);

        this.closeDialog({ success: true });
      },
      error: () => {
        this.notificationService.error(`Aktivität NICHT gelöscht`);
      },
    });
  }

  closeDialog(result?: DialogResult) {
    this.richTextControl.reset();

    this.dialogManager.closeActivityDetails(result);
  }

  extractFilename(url: string): string {
    const pathname = new URL(url).pathname;
    return pathname;
  }

  updateActivity(): void {
    const typename = this.activity?.activityType.typeName;
    if (typename === 'Beratungsgespräch') {
      this.dialogManager.openConsultationDialog({
        activityToUpgrade: this.activity,
      });
      this.closeDialog();
    } else if (
      typename === 'Email' ||
      typename === 'EingehendeEmail' ||
      typename === 'AusgehendeEmail'
    ) {
      this.dialogManager.openEmailDialog({
        activityToUpgrade: this.activity,
      });
      this.closeDialog();
    } else if (typename === 'Abwesenheit') {
      const ref = this.dialogManager.openAbsenceDialog({
        activityToUpgrade: this.activity,
      });
      ref.onClose.subscribe((result) => {
        this.closeDialog(result);
      });
    } else {
      // Für alle übrigen Aktivitätstypen existiert noch kein Bearbeiten-Dialog
      this.closeDialog();
    }
  }

  routeToFeedback(feedbackId: number) {
    this.router.navigate(['feedback', feedbackId]);
    this.closeDialog();
  }
}
